import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, LogOut, Sparkles, UploadCloud, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  company_name: '',
  sector_template: 'clinic',
  owner_name: '',
  owner_email: '',
  primary_color: '#0A1A3F',
  accent_color: '#00E5FF',
  tagline: '',
};

const templateDefaults = {
  clinic: [
    { role: 'doctora', nombre: '', email: '' },
    { role: 'enfermera', nombre: '', email: '' },
    { role: 'administradora', nombre: '', email: '' },
    { role: 'community_manager', nombre: '', email: '' },
  ],
  partner: [
    { role: 'partner', nombre: '', email: '' },
    { role: 'user', nombre: '', email: '' },
  ],
  agency: [
    { role: 'agencia', nombre: '', email: '' },
    { role: 'user', nombre: '', email: '' },
  ],
};

const moduleOptions = ['crm', 'agenda', 'historial', 'inventario', 'contenido', 'reporting', 'clientes'];

export default function AdminClientProvisioningPage() {
  const navigate = useNavigate();
  const { user, loading, logout, authFetch } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [createdClient, setCreatedClient] = useState(null);
  const [customUsers, setCustomUsers] = useState(templateDefaults.clinic);
  const [selectedModules, setSelectedModules] = useState(['crm', 'agenda', 'historial', 'inventario', 'contenido', 'reporting']);
  const [checklistSaving, setChecklistSaving] = useState('');
  const [periodDays, setPeriodDays] = useState(30);
  const [brandingFiles, setBrandingFiles] = useState({});
  const [brandingSaving, setBrandingSaving] = useState('');

  const loadDashboard = useCallback(async () => {
    const response = await authFetch(`${API}/admin/client-platforms/dashboard?period_days=${periodDays}`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.detail || 'No se pudo cargar el dashboard de clientes');
    setDashboard(data);
  }, [authFetch, periodDays]);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'admin') {
      navigate('/admin');
      return;
    }
    loadDashboard().catch((error) => toast.error(error.message));
  }, [loading, user, navigate, loadDashboard]);

  const selectedTemplate = useMemo(
    () => dashboard?.templates?.find((template) => template.id === form.sector_template),
    [dashboard, form.sector_template],
  );

  useEffect(() => {
    const defaults = templateDefaults[form.sector_template] || templateDefaults.clinic;
    setCustomUsers(defaults.map((item, index) => ({
      ...item,
      nombre: index === 0 ? form.owner_name : '',
      email: index === 0 ? form.owner_email : '',
    })));
    if (form.sector_template === 'clinic') setSelectedModules(['crm', 'agenda', 'historial', 'inventario', 'contenido', 'reporting']);
    if (form.sector_template === 'partner') setSelectedModules(['crm', 'contenido', 'reporting']);
    if (form.sector_template === 'agency') setSelectedModules(['crm', 'contenido', 'reporting', 'clientes']);
  }, [form.sector_template]);

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const response = await authFetch(`${API}/admin/client-platforms/provision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, modules_enabled: selectedModules, custom_users: customUsers }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'No se pudo aprovisionar el cliente');
      setCreatedClient(data);
      toast.success(`Cliente ${data.client.client_id} creado`);
      setForm(initialForm);
      await loadDashboard();
    } catch (error) {
      toast.error(error.message || 'Error aprovisionando cliente');
    } finally {
      setSaving(false);
    }
  };

  const toggleModule = (moduleId) => {
    setSelectedModules((current) => current.includes(moduleId) ? current.filter((item) => item !== moduleId) : [...current, moduleId]);
  };

  const updateChecklist = async (clientId, checklist, itemId) => {
    setChecklistSaving(itemId);
    try {
      const nextChecklist = checklist.map((item) => item.id === itemId ? { ...item, done: !item.done } : item);
      const response = await authFetch(`${API}/admin/client-platforms/${clientId}/checklist`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ checklist: nextChecklist }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'No se pudo actualizar el checklist');
      await loadDashboard();
    } catch (error) {
      toast.error(error.message || 'Error actualizando checklist');
    } finally {
      setChecklistSaving('');
    }
  };

  const uploadBranding = async (clientId) => {
    const file = brandingFiles[clientId];
    if (!file) return;
    setBrandingSaving(clientId);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      const response = await authFetch(`${API}/admin/client-platforms/${clientId}/branding-assets`, {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'No se pudo subir el logo');
      toast.success('Logo cargado');
      setBrandingFiles((current) => ({ ...current, [clientId]: null }));
      await loadDashboard();
    } catch (error) {
      toast.error(error.message || 'Error subiendo logo');
    } finally {
      setBrandingSaving('');
    }
  };

  if (loading || !dashboard) {
    return <main className="min-h-screen bg-[#07111f] p-8 text-white" data-testid="admin-client-provisioning-loading">Cargando aprovisionamiento…</main>;
  }

  return (
    <main className="min-h-screen bg-[radial-gradient(circle_at_top,_#0b1f3f_0%,_#07111f_52%,_#020817_100%)] px-4 py-8 sm:px-6 lg:px-8" data-testid="admin-client-provisioning-page">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-[28px] border border-white/10 bg-white/5 p-5 text-white backdrop-blur">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-cyan-200">Alta de cliente en 3 minutos</p>
            <h1 className="mt-2 text-4xl font-black">Provisión guiada de plataformas</h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-300">Genera `client_id`, branding, usuarios base y acceso provisional para clínica, partner o agencia.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => navigate('/admin')} className="rounded-full border border-white/20 px-4 py-2 text-sm font-semibold text-white" data-testid="admin-client-provisioning-back-button">Volver a admin</button>
            <button type="button" onClick={logout} className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-900" data-testid="admin-client-provisioning-logout-button"><LogOut className="mr-2 inline h-4 w-4" />Salir</button>
          </div>
        </div>

        <section className="grid gap-5 xl:grid-cols-[0.92fr_1.08fr]">
          <form onSubmit={submit} className="rounded-[28px] border border-white/10 bg-white/8 p-5 text-white backdrop-blur sm:p-6" data-testid="admin-client-provisioning-form">
            <div className="flex items-center gap-3">
              <div className="rounded-2xl bg-cyan-400/15 p-3"><Sparkles className="h-5 w-5 text-cyan-200" /></div>
              <div>
                <p className="text-xs uppercase tracking-[0.22em] text-cyan-200">Wizard</p>
                <h2 className="text-2xl font-black">Nuevo cliente / plataforma</h2>
              </div>
            </div>
            <div className="mt-5 space-y-4">
              <input data-testid="provision-company-name" className="w-full rounded-2xl border border-white/10 bg-slate-950/40 px-4 py-3" placeholder="Empresa o cliente" value={form.company_name} onChange={(event) => setForm((prev) => ({ ...prev, company_name: event.target.value }))} required />
              <div className="grid gap-4 sm:grid-cols-2">
                <select data-testid="provision-sector-template" className="rounded-2xl border border-white/10 bg-slate-950/40 px-4 py-3" value={form.sector_template} onChange={(event) => setForm((prev) => ({ ...prev, sector_template: event.target.value }))}>
                  {dashboard.templates.map((template) => <option key={template.id} value={template.id}>{template.id}</option>)}
                </select>
                <input data-testid="provision-owner-name" className="rounded-2xl border border-white/10 bg-slate-950/40 px-4 py-3" placeholder="Responsable principal" value={form.owner_name} onChange={(event) => setForm((prev) => ({ ...prev, owner_name: event.target.value }))} required />
              </div>
              <input data-testid="provision-owner-email" type="email" className="w-full rounded-2xl border border-white/10 bg-slate-950/40 px-4 py-3" placeholder="Email responsable (opcional pero recomendado)" value={form.owner_email} onChange={(event) => setForm((prev) => ({ ...prev, owner_email: event.target.value }))} />
              <textarea data-testid="provision-tagline" className="min-h-[100px] w-full rounded-2xl border border-white/10 bg-slate-950/40 px-4 py-3" placeholder="Tagline o promesa de marca" value={form.tagline} onChange={(event) => setForm((prev) => ({ ...prev, tagline: event.target.value }))} />
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="rounded-2xl border border-white/10 bg-slate-950/40 px-4 py-3 text-sm text-slate-200" data-testid="provision-primary-color-wrapper">Color principal<input data-testid="provision-primary-color" type="color" className="mt-2 block h-10 w-full rounded-xl border-none bg-transparent" value={form.primary_color} onChange={(event) => setForm((prev) => ({ ...prev, primary_color: event.target.value }))} /></label>
                <label className="rounded-2xl border border-white/10 bg-slate-950/40 px-4 py-3 text-sm text-slate-200" data-testid="provision-accent-color-wrapper">Color acento<input data-testid="provision-accent-color" type="color" className="mt-2 block h-10 w-full rounded-xl border-none bg-transparent" value={form.accent_color} onChange={(event) => setForm((prev) => ({ ...prev, accent_color: event.target.value }))} /></label>
              </div>
              <div className="rounded-[24px] border border-cyan-400/20 bg-cyan-400/8 p-4 text-sm text-cyan-50" data-testid="provision-template-preview">
                <p className="font-semibold">Plantilla seleccionada</p>
                <p className="mt-2">Login provisional: <span className="font-bold">{selectedTemplate?.portal_route}</span> · Panel: <span className="font-bold">{selectedTemplate?.workspace_route}</span></p>
              </div>
              <div className="rounded-[24px] border border-white/10 bg-slate-950/30 p-4" data-testid="provision-modules-box">
                <p className="text-sm font-semibold text-white">Módulos activados</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {moduleOptions.map((moduleId) => (
                    <button key={moduleId} type="button" onClick={() => toggleModule(moduleId)} className={`rounded-full px-4 py-2 text-xs font-semibold ${selectedModules.includes(moduleId) ? 'bg-cyan-300 text-slate-950' : 'bg-white/10 text-slate-200'}`} data-testid={`provision-module-${moduleId}`}>
                      {moduleId}
                    </button>
                  ))}
                </div>
              </div>
              <div className="rounded-[24px] border border-white/10 bg-slate-950/30 p-4" data-testid="provision-custom-users-box">
                <p className="text-sm font-semibold text-white">Usuarios base reales</p>
                <div className="mt-3 space-y-3">
                  {customUsers.map((userRow, index) => (
                    <div key={`${userRow.role}-${index}`} className="grid gap-3 md:grid-cols-[120px_1fr_1fr]" data-testid={`provision-custom-user-${userRow.role}`}>
                      <div className="rounded-2xl bg-white/10 px-4 py-3 text-sm font-semibold text-cyan-100">{userRow.role}</div>
                      <input className="rounded-2xl border border-white/10 bg-slate-950/40 px-4 py-3" placeholder="Nombre real" value={userRow.nombre} onChange={(event) => setCustomUsers((current) => current.map((item, currentIndex) => currentIndex === index ? { ...item, nombre: event.target.value } : item))} data-testid={`provision-custom-user-name-${userRow.role}`} />
                      <input className="rounded-2xl border border-white/10 bg-slate-950/40 px-4 py-3" placeholder="Email real" value={userRow.email} onChange={(event) => setCustomUsers((current) => current.map((item, currentIndex) => currentIndex === index ? { ...item, email: event.target.value } : item))} data-testid={`provision-custom-user-email-${userRow.role}`} />
                    </div>
                  ))}
                </div>
              </div>
              <button data-testid="provision-submit-button" type="submit" className="w-full rounded-full bg-cyan-300 px-5 py-3 text-sm font-semibold text-slate-950" disabled={saving}>{saving ? 'Aprovisionando…' : 'Crear plataforma'}</button>
            </div>
          </form>

          <div className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-3">
              <article className="rounded-[24px] border border-white/10 bg-white/8 p-5 text-white backdrop-blur" data-testid="provision-summary-platforms"><p className="text-xs uppercase tracking-[0.2em] text-slate-300">Plataformas</p><p className="mt-3 text-3xl font-black">{dashboard.summary.platforms}</p></article>
              <article className="rounded-[24px] border border-white/10 bg-white/8 p-5 text-white backdrop-blur" data-testid="provision-summary-users"><p className="text-xs uppercase tracking-[0.2em] text-slate-300">Usuarios</p><p className="mt-3 text-3xl font-black">{dashboard.summary.users}</p></article>
              <article className="rounded-[24px] border border-white/10 bg-white/8 p-5 text-white backdrop-blur" data-testid="provision-summary-clinic"><p className="text-xs uppercase tracking-[0.2em] text-slate-300">Clínica viva</p><p className="mt-3 text-3xl font-black">{dashboard.summary.clinic_ready ? 'Sí' : 'No'}</p></article>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="provision-period-switcher">
              {[7, 30, 90].map((days) => (
                <button key={days} type="button" onClick={() => setPeriodDays(days)} className={`rounded-full px-4 py-2 text-sm font-semibold ${periodDays === days ? 'bg-cyan-300 text-slate-950' : 'bg-white/10 text-white'}`} data-testid={`provision-period-${days}`}>
                  {days} días
                </button>
              ))}
            </div>

            {createdClient && (
              <article className="rounded-[28px] border border-emerald-400/20 bg-emerald-400/10 p-5 text-white" data-testid="created-client-card">
                <div className="flex items-center gap-3"><UploadCloud className="h-5 w-5 text-emerald-200" /><h3 className="text-xl font-black">Cliente creado: {createdClient.client.client_id}</h3></div>
                <p className="mt-2 text-sm text-emerald-50">Acceso provisional: <span className="font-bold">{createdClient.client.links.login_url}</span></p>
                <div className="mt-4 space-y-3" data-testid="created-client-users-list">
                  {createdClient.created_users.map((createdUser) => (
                    <div key={`${createdUser.email}-${createdUser.role}`} className="rounded-[22px] bg-black/20 p-4" data-testid={`created-client-user-${createdUser.role}`}>
                      <p className="font-semibold">{createdUser.role} · {createdUser.nombre}</p>
                      <p className="text-sm text-slate-100">{createdUser.email}</p>
                      <p className="mt-1 text-xs text-cyan-100">Password temporal: {createdUser.temporary_password}</p>
                    </div>
                  ))}
                </div>
              </article>
            )}

            <section className="space-y-4" data-testid="client-platforms-list">
              {dashboard.platforms.map((platform) => (
                <article key={platform.client_id} className="rounded-[28px] border border-white/10 bg-white/8 p-5 text-white backdrop-blur" data-testid={`client-platform-card-${platform.client_id}`}>
                  <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-cyan-200">{platform.client_id}</p>
                      <h3 className="mt-2 text-2xl font-black">{platform.display_name}</h3>
                      <p className="mt-1 text-sm text-slate-300">{platform.sector_template} · {platform.segment_label}</p>
                    </div>
                    <div className="rounded-[22px] px-4 py-3 text-sm" style={{ background: `${platform.branding?.primary_color || '#0A1A3F'}22`, border: `1px solid ${platform.branding?.accent_color || '#00E5FF'}55` }} data-testid={`client-platform-branding-${platform.client_id}`}>
                      {platform.branding?.logo_url && <img src={platform.branding.logo_url} alt={platform.display_name} className="mb-3 h-12 w-12 rounded-2xl object-cover" data-testid={`client-platform-logo-${platform.client_id}`} />}
                      <p>{platform.branding?.tagline || 'Sin tagline aún'}</p>
                    </div>
                  </div>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    <div className="rounded-[22px] bg-black/20 p-4" data-testid={`client-platform-metric-users-${platform.client_id}`}><p className="text-xs uppercase tracking-[0.2em] text-slate-300">Usuarios</p><p className="mt-2 text-2xl font-black">{platform.metrics.users}</p></div>
                    <div className="rounded-[22px] bg-black/20 p-4" data-testid={`client-platform-metric-logins-${platform.client_id}`}><p className="text-xs uppercase tracking-[0.2em] text-slate-300">Accesos {periodDays}d</p><p className="mt-2 text-2xl font-black">{platform.metrics.recent_logins_period}</p></div>
                    <div className="rounded-[22px] bg-black/20 p-4" data-testid={`client-platform-metric-appointments-${platform.client_id}`}><p className="text-xs uppercase tracking-[0.2em] text-slate-300">Citas</p><p className="mt-2 text-2xl font-black">{platform.metrics.appointments}</p></div>
                    <div className="rounded-[22px] bg-black/20 p-4" data-testid={`client-platform-metric-histories-${platform.client_id}`}><p className="text-xs uppercase tracking-[0.2em] text-slate-300">Historiales</p><p className="mt-2 text-2xl font-black">{platform.metrics.histories}</p></div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2 text-xs" data-testid={`client-platform-links-${platform.client_id}`}>
                    <a href={platform.links.login_url} target="_blank" rel="noreferrer" className="rounded-full bg-white px-4 py-2 font-semibold text-slate-900">Acceso provisional</a>
                    <a href={`${API}/admin/client-platforms/${platform.client_id}/summary.pdf?period_days=${periodDays}`} target="_blank" rel="noreferrer" className="rounded-full bg-cyan-300 px-4 py-2 font-semibold text-slate-950" data-testid={`client-platform-pdf-${platform.client_id}`}>PDF resumen</a>
                    <span className="rounded-full bg-slate-800 px-4 py-2">Email: {platform.channel_readiness.email}</span>
                    <span className="rounded-full bg-slate-800 px-4 py-2">WhatsApp: {platform.channel_readiness.whatsapp}</span>
                    <span className="rounded-full bg-slate-800 px-4 py-2">Social scheduler: {platform.channel_readiness.social_scheduler}</span>
                  </div>
                  <div className="mt-4 rounded-[22px] border border-white/10 bg-black/20 p-4" data-testid={`client-platform-branding-upload-${platform.client_id}`}>
                    <p className="text-sm font-semibold text-white">Assets / branding</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <input type="file" accept="image/*" className="rounded-2xl border border-white/10 bg-slate-950/40 px-4 py-3 text-sm" onChange={(event) => setBrandingFiles((current) => ({ ...current, [platform.client_id]: event.target.files?.[0] || null }))} data-testid={`client-platform-logo-input-${platform.client_id}`} />
                      <button type="button" onClick={() => uploadBranding(platform.client_id)} className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-900" data-testid={`client-platform-logo-upload-${platform.client_id}`} disabled={!brandingFiles[platform.client_id] || brandingSaving === platform.client_id}>
                        {brandingSaving === platform.client_id ? 'Subiendo…' : 'Subir logo'}
                      </button>
                    </div>
                  </div>
                  {!!platform.template_upload && (
                    <div className="mt-4 rounded-[22px] border border-cyan-400/20 bg-cyan-400/8 p-4 text-sm text-cyan-50" data-testid={`client-platform-template-upload-${platform.client_id}`}>
                      Excel activo: {platform.template_upload.original_filename} · {platform.template_upload.sheet_names?.join(', ')}
                    </div>
                  )}
                  <div className="mt-4 rounded-[22px] border border-white/10 bg-black/20 p-4" data-testid={`client-platform-checklist-${platform.client_id}`}>
                    <p className="text-sm font-semibold text-white">Checklist de lanzamiento</p>
                    <div className="mt-3 grid gap-2 md:grid-cols-2">
                      {(platform.launch_checklist || []).map((item) => (
                        <button key={item.id} type="button" onClick={() => updateChecklist(platform.client_id, platform.launch_checklist, item.id)} className={`flex items-center justify-between rounded-2xl px-4 py-3 text-left text-sm ${item.done ? 'bg-emerald-400/15 text-emerald-100' : 'bg-white/8 text-slate-200'}`} data-testid={`client-platform-checklist-item-${platform.client_id}-${item.id}`}>
                          <span>{item.label}</span>
                          <span>{checklistSaving === item.id ? '…' : item.done ? '✓' : '○'}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="mt-4 rounded-[22px] border border-white/10 bg-black/20 p-4" data-testid={`client-platform-modules-${platform.client_id}`}>
                    <p className="text-sm font-semibold text-white">Permisos / módulos</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {(platform.modules_enabled || []).map((moduleId) => <span key={moduleId} className="rounded-full bg-cyan-300/15 px-3 py-2 text-xs font-semibold text-cyan-100">{moduleId}</span>)}
                    </div>
                  </div>
                </article>
              ))}
            </section>
          </div>
        </section>
      </div>
    </main>
  );
}
